import { useEffect, useState } from "react";
import {
    AppBar,
    Toolbar,
    Stack,
    IconButton,
    Box,
    Typography,
    Button,
    Divider,
    MenuList,
    MenuItem,
    CircularProgress,
    Tooltip,
    Skeleton,
    useTheme,
} from "@mui/material";

import { RootState } from "@store/store";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { BotonMenu } from "./BotonMenu";
import { useDialogs } from "@toolpad/core/useDialogs";
import { Notificacion } from "./DialogosSegmentos/Notificacion";

import { DashboardHeaderProps } from "@toolpad/core/DashboardLayout";
import { usePostExtractoProspectosMutation } from "@services/api/extras";
import { CampoBusquedaSolicitudes } from "./CampoBusquedaSolicitudes";
import { CambiarVistaCompania } from "./DialogosSegmentos/CambiarVistaCompania";
import { DialogType, OptionApplicationMenu, PopupType } from "@interfaces/slices";
import { Account, AccountPreview, AccountPopoverFooter, SignOutButton } from "@toolpad/core/Account";
import { useLazyGetContractDocsZipLinkQuery, usePostDocumentsZipLinkMutation } from "@services/api/documents";
import {
    setDialogConfig,
    setOptionAppSelected,
    setPopupConfig,
    setPrintingMode,
    setReloadSection,
} from "@store/slices/componentsSlice";

import ReplayIcon from "@mui/icons-material/Replay";
import CommentIcon from "@assets/empty_comments_icon.svg";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { DescargarReporte } from "./DialogosSegmentos/DescargarReporte";
import { useLazyGetCompanyQuery } from "@services/api/companies";
import { UUID } from "crypto";
import { setDownloadingList } from "@store/slices/sessionSlice";

export const CustomHeader = (props: DashboardHeaderProps) => {
    const theme = useTheme();
    const dialogs = useDialogs();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [downloadingDocs, setDownloadingDocs] = useState<boolean>(false);

    const session = useSelector((state: RootState) => state.session);
    const permissions = useSelector((state: RootState) => state.session.permissions);
    const companiesUser = useSelector((state: RootState) => state.session.companies);
    const financialPyMIOUser = useSelector((state: RootState) => state.session.financial_inc);
    const reloadSection = useSelector((state: RootState) => state.components.currentSection.reload);
    const expandLayoutSidebarState = useSelector((state: RootState) => state.components.layout.sidebar);
    const lockMenuApplication = useSelector((state: RootState) => state.components.menuApplication.lock);
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);

    const [triggerCompanyInfo, { isLoading: isLoadingCompanyInfo }] = useLazyGetCompanyQuery();
    const [triggerPostDocsZipLink] = usePostDocumentsZipLinkMutation();
    const [triggerPostExtractoProspectos] = usePostExtractoProspectosMutation();
    const [triggerGetContractDocsZipLink] = useLazyGetContractDocsZipLinkQuery();

    const handleOnReloadSectionClicked = () => {
        dispatch(setReloadSection(true));
    };

    const handleOnOptionSelected = async (option: { key: any; value: any }) => {
        switch (option.key) {
            case "DOCEXP":
                try {
                    setDownloadingDocs(true);
                    await triggerPostDocsZipLink({
                        applicationId: applicationSelected.id,
                        userId: session.user.id,
                    }).unwrap();

                    await dialogs.open(Notificacion, {
                        type: "INFO",
                        text: "La solicitud de generación del expediente se realizo con éxito, en breve llegará un correo con el link para su descarga.",
                    });
                } catch (error) {
                    console.log("error: %s", error);
                    await dialogs.open(Notificacion, {
                        type: "WARNING",
                        text: "No se pudo obtener el zip del expediente.",
                    });
                }
                setDownloadingDocs(false);
                break;
            case "DOCVAL":
                try {
                    setDownloadingDocs(true);
                    const link = await triggerGetContractDocsZipLink({
                        applicationId: applicationSelected.id,
                    }).unwrap();

                    const linkElement = document.createElement("a");
                    linkElement.href = link;

                    document.body.appendChild(linkElement);

                    linkElement.click();

                    document.body.removeChild(linkElement);
                } catch (error: any) {
                    await dialogs.open(Notificacion, {
                        type: "WARNING",
                        text: "No se pudo obtener el zip con los documentos.",
                    });
                }
                setDownloadingDocs(false);
                break;
            case "REPCRE":
                dispatch(setPrintingMode(true));
                // dispatch(
                //     setDialogConfig({
                //         shown: true,
                //         fullscreen: true,
                //         type: DialogType.DESCARGAR_REPORTE,
                //         props: undefined,
                //     }),
                // );
                await dialogs.open(DescargarReporte, {
                    fullScreen: true,
                });
                break;
            case "REPPLD":
                dispatch(setPrintingMode(true));
                dispatch(
                    setDialogConfig({
                        shown: true,
                        fullscreen: true,
                        type: DialogType.DESCARGAR_REPORTE_PLD,
                        props: undefined,
                    }),
                );
                break;
            case "EXTPRO":
                try {
                    const result = await triggerPostExtractoProspectos(applicationSelected.id).unwrap();

                    const fullBase64 = result.data.startsWith("data:")
                        ? result.data
                        : `data:text/csv;base64,${result.data}`;
                    const link = document.createElement("a");
                    link.href = fullBase64;
                    link.download = `prospectos_${applicationSelected.company_source.toLowerCase()}_${applicationSelected.id_application_company}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } catch (error) {
                    console.log("error: %s", JSON.stringify(error));

                    await dialogs.open(Notificacion, {
                        type: "WARNING",
                        text: "Ocurrio un error al generar el CSV de prospectos.",
                    });
                }
                break;
            case "LISSOL":
                dispatch(setDownloadingList(true));
                break;
        }
    };

    const handleOnCommentsClicked = () => {
        const item = document.querySelector("#content");

        dispatch(
            setPopupConfig({
                type: PopupType.COMENTARIOS,
                anchorEl: "gralComments",
                props: undefined,
                placement: "bottom-end",
            }),
        );
    };

    const handleCompanyViewChangeClicked = async () => {
        await dialogs.open(CambiarVistaCompania, {
            fullWidth: true,
            maxWidth: "md",
        });
    };

    const handleAdmin = () => {
        dispatch(setOptionAppSelected(OptionApplicationMenu.USUARIOS));
        navigate("/admin");
    };

    const handleOnClickReturnDashboards = () => {
        dispatch(setDialogConfig());
        dispatch(setPopupConfig());
        navigate("/solicitudes");
    };

    const loadInfo = async (companyId: UUID) => {
        const company = await triggerCompanyInfo(companyId).unwrap();
        const image = company.images.interface.navbar.logo;

        setLogoUrl(image == null ? null : `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${image}`);
    };

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0") return;

        let companyId: UUID = "0-0-0-0-0";
        if (["/solicitudes", "/admin"].includes(location.pathname)) {
            const principal = session.companies?.find((company) => company.principal === true);
            if (principal) {
                companyId = principal.id;
            }
        }

        if (location.pathname.startsWith("/solicitud/")) {
            companyId = applicationSelected.company_id;
        }
        loadInfo(companyId);
    }, [applicationSelected.id, location.pathname]);

    useEffect(() => {
        props.onToggleMenu(expandLayoutSidebarState.expandState);
    }, [expandLayoutSidebarState.expandState]);

    return (
        <AppBar
            position="absolute"
            sx={{
                zIndex: 1201,
                boxShadow: ["/"].includes(location.pathname) ? "none" : "0px 5.5px 5.5px 0px rgba(0, 0, 0, 0.05)",
                background: "#fff",
            }}
        >
            <Toolbar>
                <Stack
                    direction={"row"}
                    spacing={2}
                    sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                    }}
                >
                    {["/solicitudes"].includes(location.pathname) === false && (
                        <IconButton onClick={handleOnClickReturnDashboards} disabled={lockMenuApplication}>
                            <ArrowBackIosNewIcon color={"main4"} />
                        </IconButton>
                    )}
                    {isLoadingCompanyInfo ? (
                        <Skeleton variant="rectangular" width={"110px"} />
                    ) : (
                        <Box
                            component="img"
                            src={logoUrl}
                            // alt={applicationSelected.company_source}
                            sx={{ width: 110, cursor: "pointer" }}
                            onClick={() => (lockMenuApplication ? () => {} : handleOnClickReturnDashboards())}
                        />
                    )}
                    {["/solicitudes"].includes(location.pathname) && <CampoBusquedaSolicitudes />}
                    {["/solicitudes", "/admin"].includes(location.pathname) === false && (
                        <>
                            <Typography
                                variant="h4"
                                color="main3"
                                component={"span"}
                                noWrap
                                sx={{
                                    pl: 4,
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%"
                                }}>
                                {applicationSelected.company_rfc} - {applicationSelected.company_name} -{" "}
                                {applicationSelected.comercial_name}
                            </Typography>
                            <Tooltip title="Recargar sección">
                                <IconButton
                                    color="main2"
                                    onClick={handleOnReloadSectionClicked}
                                    disabled={lockMenuApplication}
                                    loading={reloadSection}
                                    loadingIndicator={<CircularProgress size={"2rem"} />}
                                >
                                    <ReplayIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}

                    <Stack direction={"row"} spacing={2} sx={{ alignItems: "center" }}>
                        {["/solicitudes"].includes(location.pathname) && (
                            <BotonMenu
                                iconButton={<SystemUpdateAltIcon />}
                                textButton="Descargar"
                                loading={downloadingDocs}
                                options={[{ key: "LISSOL", value: "Listado Completo de Solicitudes" }]}
                                handleOnOptionSelect={handleOnOptionSelected}
                            />
                        )}
                        {["/solicitudes", "/admin"].includes(location.pathname) === false && (
                            <>
                                {permissions.find((p) => p.module.code === "ACCDR")?.can_watch === true && (
                                    <BotonMenu
                                        iconButton={<SystemUpdateAltIcon />}
                                        textButton="Descargar"
                                        loading={downloadingDocs}
                                        options={[
                                            { key: "DOCEXP", value: "Expediente" },
                                            { key: "DOCVAL", value: "Documentos Valor" },
                                            { key: "REPCRE", value: "Reporte Crediticio" },
                                            { key: "REPPLD", value: "Reporte PLD" },
                                            { key: "EXTPRO", value: "Prospectos" },
                                        ]}
                                        handleOnOptionSelect={handleOnOptionSelected}
                                    />
                                )}
                                {permissions.find((p) => p.module.code === "MCOGRAL")?.can_watch === true && (
                                    <Button
                                        id="gralComments"
                                        variant="contained"
                                        endIcon={
                                            <img
                                                src={CommentIcon}
                                                style={{
                                                    width: "20px",
                                                    height: "20px",
                                                    filter: "invert(99%) sepia(0%) saturate(4%) hue-rotate(144deg) brightness(180%)",
                                                }}
                                            />
                                        }
                                        onClick={handleOnCommentsClicked}
                                    >
                                        Comentarios
                                    </Button>
                                )}
                            </>
                        )}
                        <Account
                            localeText={{
                                accountSignOutLabel: "Cerrar sesión",
                            }}
                            slots={{
                                popoverContent: () => (
                                    <Stack>
                                        <AccountPreview variant="expanded" />
                                        <Divider />
                                        <MenuList>
                                            {permissions.find((t) => t.module.code === "MADMIN") !== undefined && (
                                                <MenuItem onClick={handleAdmin}>Administración</MenuItem>
                                            )}

                                            {(companiesUser.length > 1 || financialPyMIOUser.length > 1) && (
                                                <MenuItem onClick={handleCompanyViewChangeClicked}>
                                                    Cambiar Vista Empresa
                                                </MenuItem>
                                            )}
                                        </MenuList>
                                        <AccountPopoverFooter>
                                            <SignOutButton />
                                        </AccountPopoverFooter>
                                    </Stack>
                                ),
                            }}
                        />
                    </Stack>
                </Stack>
            </Toolbar>
        </AppBar>
    );
};
